import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, CreditCard, Mail, Lock, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useInventory } from '../context/InventoryContext';
import Seo from '../components/Seo';
import { toast } from 'sonner';

const ETRANSFER_EMAIL = 'vialratpayments@gmail.com';

const generateOrderRef = () => {
  const s = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let out = 'VRR-';
  for (let i = 0; i < 6; i++) out += s[Math.floor(Math.random() * s.length)];
  return out;
};

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

const Checkout = () => {
  const { items, subtotal, discounts, discountTotal, total, clear } = useCart();
  const { applyLocalDecrement } = useInventory();
  const navigate = useNavigate();
  const [method, setMethod] = useState('etransfer');
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [fulfillment, setFulfillment] = useState('ship'); // 'ship' | 'pickup'
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', province: 'ON', postal: '', notes: ''
  });

  useEffect(() => {
    if (items.length === 0) {
      // redirect back to shop if empty
    }
  }, [items]);

  const baseShipping = total > 0 ? 20 : 0;
  const isPickup = fulfillment === 'pickup';
  const shipping = isPickup ? 0 : baseShipping;
  const grandTotal = total + shipping;

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Address fields aren't required for local pickup; keep contact fields required.
  const required = isPickup
    ? ['firstName', 'lastName', 'email']
    : ['firstName', 'lastName', 'email', 'address', 'city', 'postal'];
  const isValid = required.every(k => form[k].trim().length > 0) && /\S+@\S+\.\S+/.test(form.email) && acknowledged;

  const submit = async (e) => {
    e.preventDefault();
    if (!acknowledged) {
      toast.error('Please acknowledge the research-use terms to continue.');
      return;
    }
    if (!isValid) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    const orderPayload = {
      items: items.map(i => ({
        slug: i.slug,
        variant_label: i.variantLabel || null,
        qty: i.qty,
        name: i.name,
        dose: i.dose,
        price: i.price,
        price_display: i.priceDisplay,
      })),
      customer: { ...form },
      fulfillment: isPickup ? 'LOCAL_PICKUP' : 'SHIP',
      method: 'INTERAC_ETRANSFER',
      subtotal: subtotal,
      discount_total: discountTotal,
      discounts: discounts,
      shipping,
      total: grandTotal,
      acknowledged: true,
    };
    let serverOrder;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Order could not be placed. Please try again.');
        setSubmitting(false);
        return;
      }
      serverOrder = data;
    } catch (err) {
      toast.error('Network error placing order. Please try again.');
      setSubmitting(false);
      return;
    }

    // Cache order locally so the /order/:ref confirmation page renders offline.
    const orderForClient = {
      ref: serverOrder.ref,
      createdAt: serverOrder.created_at,
      status: serverOrder.status,
      method: serverOrder.method,
      fulfillment: serverOrder.fulfillment,
      customer: serverOrder.customer,
      items: items.map(i => ({ slug: i.slug, name: i.name, dose: i.dose, variantLabel: i.variantLabel || null, preorder: !!i.preorder, qty: i.qty, price: i.price, priceDisplay: i.priceDisplay })),
      subtotal: serverOrder.subtotal,
      discount_total: serverOrder.discount_total || discountTotal,
      discounts: serverOrder.discounts || discounts,
      shipping: serverOrder.shipping,
      total: serverOrder.total,
    };
    try {
      const raw = localStorage.getItem('vrr_orders');
      const list = raw ? JSON.parse(raw) : [];
      list.unshift(orderForClient);
      localStorage.setItem('vrr_orders', JSON.stringify(list.slice(0, 50)));
    } catch (err) { /* ignore */ }
    // Reflect the server-side decrement in the local inventory map immediately.
    applyLocalDecrement(items.map(i => ({ slug: i.slug, variant_label: i.variantLabel || null, qty: i.qty })));
    clear();
    setTimeout(() => navigate(`/order/${serverOrder.ref}`), 300);
  };

  if (items.length === 0) {
    return (
      <main className="max-w-[900px] mx-auto px-6 md:px-10 pt-20 pb-24 text-center">
        <Seo title="Checkout | Vial Rat Research Co." description="Secure checkout." canonicalPath="/checkout" noindex />
        <h1 className="font-serif-display text-white text-5xl mb-4">Nothing to check out</h1>
        <p className="text-neutral-400 mb-8">Your bench is empty. Add some specimens first.</p>
        <Link to="/shop" className="btn-pink inline-block px-8 py-4 rounded font-mono-label text-[11px]">BROWSE CATALOG</Link>
      </main>
    );
  }

  return (
    <main className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-20">
      <Seo title="Checkout | Vial Rat Research Co." description="Secure checkout." canonicalPath="/checkout" noindex />
      <Link to="/shop" className="inline-flex items-center gap-2 font-mono-label text-[11px] text-[#ff2fa1] hover:text-white transition-colors">
        <ArrowLeft size={14} /> CONTINUE SHOPPING
      </Link>
      <div className="mt-6 font-mono-label text-[11px] text-[#ff2fa1] mb-2">CHECKOUT</div>
      <h1 className="font-serif-display text-white text-5xl md:text-6xl">Complete Your Order</h1>

      <form onSubmit={submit} className="mt-12 grid lg:grid-cols-[1fr_420px] gap-10 items-start">
        {/* Left column */}
        <div className="space-y-10">
          {/* Contact */}
          <section>
            <h2 className="font-serif-display text-white text-2xl mb-6">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="First Name" required value={form.firstName} onChange={v => setField('firstName', v)} />
              <Field label="Last Name" required value={form.lastName} onChange={v => setField('lastName', v)} />
              <Field label="Email" required type="email" value={form.email} onChange={v => setField('email', v)} />
              <Field label="Phone (optional)" type="tel" value={form.phone} onChange={v => setField('phone', v)} />
            </div>
          </section>

          {/* Fulfillment Method */}
          <section>
            <h2 className="font-serif-display text-white text-2xl mb-6">Fulfillment</h2>
            <div className="space-y-3">
              <PayOption
                active={fulfillment === 'ship'}
                onClick={() => setFulfillment('ship')}
                icon={<CreditCard size={20} />} title="Canada Post — Tracked" badge={`$${baseShipping.toFixed(2)}`}
                desc="Shipped from within Canada in plain packaging."
              />
              <PayOption
                active={fulfillment === 'pickup'}
                onClick={() => setFulfillment('pickup')}
                icon={<Mail size={20} />} title="Local Pickup" badge="FREE"
                desc="Available in select areas of Northern BC and Northern Alberta by prior arrangement. Please contact us before selecting Local Pickup. Pickup details will be provided after your order is confirmed."
              />
            </div>
          </section>

          {/* Shipping Address (only when shipping is selected) */}
          {!isPickup && (
            <section>
              <h2 className="font-serif-display text-white text-2xl mb-6">Shipping Address <span className="font-mono-label text-[11px] text-neutral-500 ml-2">CANADA ONLY</span></h2>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Field label="Street Address" required value={form.address} onChange={v => setField('address', v)} />
                </div>
                <Field label="City" required value={form.city} onChange={v => setField('city', v)} />
                <div>
                  <label className="font-mono-label text-[10px] text-neutral-400 block mb-2">PROVINCE <span className="text-[#ff2fa1]">*</span></label>
                  <select value={form.province} onChange={e => setField('province', e.target.value)}
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#ff2fa1] outline-none py-3 text-white transition-colors">
                    {PROVINCES.map(p => <option key={p} value={p} className="bg-[#0a0a0b]">{p}</option>)}
                  </select>
                </div>
                <Field label="Postal Code" required value={form.postal} onChange={v => setField('postal', v.toUpperCase())} />
                <div className="md:col-span-2">
                  <Field label="Order Notes (optional)" value={form.notes} onChange={v => setField('notes', v)} />
                </div>
              </div>
            </section>
          )}
          {isPickup && (
            <section>
              <h2 className="font-serif-display text-white text-2xl mb-6">Pickup Notes <span className="font-mono-label text-[11px] text-neutral-500 ml-2">OPTIONAL</span></h2>
              <p className="text-sm text-neutral-400 mb-4">You've chosen Local Pickup. We'll email you privately to arrange a pickup time and location after your order is confirmed.</p>
              <Field label="Notes for the store owner (optional)" value={form.notes} onChange={v => setField('notes', v)} />
            </section>
          )}

          {/* Payment */}
          <section>
            <h2 className="font-serif-display text-white text-2xl mb-6">Payment Method</h2>
            <div className="space-y-3">
              <PayOption
                active={method === 'etransfer'} onClick={() => setMethod('etransfer')}
                icon={<Mail size={20} />} title="Interac e-Transfer" badge="RECOMMENDED"
                desc={<>Send an e-Transfer to <span className="text-white font-semibold">{ETRANSFER_EMAIL}</span> after placing your order. Full instructions will be shown on the next page.</>}
              />
              {method === 'etransfer' && (
                <CopyEmailButton value={ETRANSFER_EMAIL} />
              )}
            </div>
          </section>

          {/* Research Use + Final Sale acknowledgement */}
          <section>
            <h2 className="font-serif-display text-white text-2xl mb-6">Terms & Acknowledgement</h2>
            <div className="p-5 border border-[#ff2fa1]/30 bg-[#ff2fa1]/[0.04] rounded">
              <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">RESEARCH USE ONLY</div>
              <p className="text-sm text-neutral-300 leading-relaxed">All products sold by Vial Rat Research Co. are intended strictly for laboratory and research purposes and are not intended for human consumption or medical use. By completing your purchase, you acknowledge and agree to these terms.</p>
            </div>
            <div className="mt-3 p-5 border border-white/10 rounded">
              <div className="font-mono-label text-[11px] text-neutral-300 mb-2">FINAL SALE</div>
              <p className="text-sm text-neutral-400 leading-relaxed">Due to the nature of our products, all sales are final. We do not accept returns or offer refunds except where required by applicable law. If your order arrives damaged or incorrect, please contact us so we can review the issue.</p>
            </div>
            <label className="mt-4 flex items-start gap-3 cursor-pointer group">
              <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
                data-testid="research-use-ack-checkbox"
                className="mt-1 w-4 h-4 accent-[#ff2fa1] cursor-pointer" />
              <span className="text-sm text-neutral-300 leading-relaxed">
                I acknowledge these products are for <span className="text-white">laboratory / research use only</span> and that all sales are <span className="text-white">final</span>.
              </span>
            </label>
          </section>

          <div className="hidden lg:block">
            <button type="submit" disabled={submitting || !isValid} data-testid="place-order-btn"
              className={`w-full py-5 rounded font-mono-label text-[11px] transition-colors ${submitting || !isValid ? 'bg-white/10 text-neutral-500 cursor-not-allowed' : 'btn-pink'}`}>
              {submitting ? 'PLACING ORDER…' : `PLACE ORDER · $${grandTotal.toFixed(2)}`}
            </button>
            <div className="flex items-center gap-2 mt-3 text-[11px] text-neutral-500">
              <Lock size={12} /> Your details are only saved on this device until you complete the transfer.
            </div>
          </div>
        </div>

        {/* Right column - summary */}
        <aside className="bg-[#101013] border border-white/5 rounded-lg p-6 lg:sticky lg:top-24">
          <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-5">ORDER SUMMARY</div>
          <div className="space-y-4 mb-6 max-h-[280px] overflow-y-auto pr-1">
            {items.map(i => (
              <div key={i.key} className="flex gap-3">
                <div className="w-14 h-14 rounded-md overflow-hidden bg-black/50 shrink-0 relative">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  <span className="absolute -top-1 -right-1 bg-[#ff2fa1] text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{i.qty}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-white text-sm leading-tight truncate">{i.name}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-mono-label text-[9px] text-neutral-500">{i.dose}</div>
                    {i.preorder && (
                      <span className="font-mono-label text-[9px] px-1.5 py-0.5 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">PRE-ORDER</span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-white shrink-0">${((i.price || 0) * i.qty).toFixed(2)}</div>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-white/10 space-y-2 text-sm">
            <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
            {discounts && discounts.map((d, i) => (
              <div key={i} className="flex justify-between text-[12px] text-[#ff2fa1]" data-testid={`checkout-discount-${d.code}`}>
                <span className="pr-3 leading-snug">{d.description}</span>
                <span className="whitespace-nowrap">− ${d.amount.toFixed(2)}</span>
              </div>
            ))}
            {isPickup ? (
              <Row label="Local Pickup — FREE" value={`$${shipping.toFixed(2)}`} />
            ) : (
              <Row label="Shipping (Canada, tracked)" value={`$${shipping.toFixed(2)}`} />
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="font-mono-label text-[11px] text-neutral-400">TOTAL</span>
            <span className="font-serif-display text-3xl text-white">${grandTotal.toFixed(2)}</span>
          </div>

          <div className="lg:hidden mt-6">
            <button type="submit" disabled={submitting || !isValid}
              className={`w-full py-4 rounded font-mono-label text-[11px] transition-colors ${submitting || !isValid ? 'bg-white/10 text-neutral-500 cursor-not-allowed' : 'btn-pink'}`}>
              {submitting ? 'PLACING ORDER…' : `PLACE ORDER · $${grandTotal.toFixed(2)}`}
            </button>
          </div>

          <p className="mt-5 text-[10px] text-neutral-500 font-mono-label text-center">FOR RESEARCH USE ONLY</p>
        </aside>
      </form>
    </main>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, testId }) => (
  <div>
    <label className="font-mono-label text-[10px] text-neutral-400 block mb-2">
      {label.toUpperCase()} {required && <span className="text-[#ff2fa1]">*</span>}
    </label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      data-testid={testId || `checkout-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-input`}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#ff2fa1] outline-none py-3 text-white transition-colors" />
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-neutral-300">
    <span className="text-neutral-400">{label}</span>
    <span>{value}</span>
  </div>
);

const PayOption = ({ active, onClick, icon, title, desc, badge, disabled }) => (
  <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled}
    className={`w-full text-left p-5 rounded-lg border transition-colors ${active ? 'border-[#ff2fa1] bg-[#ff2fa1]/5' : 'border-white/10 hover:border-white/30'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
    <div className="flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? 'bg-[#ff2fa1] text-black' : 'bg-white/5 text-neutral-300'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white font-semibold">{title}</span>
          {badge && <span className={`font-mono-label text-[10px] px-2 py-0.5 rounded ${active ? 'bg-[#ff2fa1] text-black' : 'border border-white/20 text-neutral-400'}`}>{badge}</span>}
        </div>
        <p className="text-sm text-neutral-400 mt-1 leading-relaxed">{desc}</p>
      </div>
      {active && <Check size={20} className="text-[#ff2fa1]" />}
    </div>
  </button>
);

export default Checkout;

const CopyEmailButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    const done = () => {
      setCopied(true);
      toast.success('Email copied — paste it into your banking app.');
      setTimeout(() => setCopied(false), 2000);
    };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {
          const ta = document.createElement('textarea');
          ta.value = value; document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); done(); } catch (_) {}
          document.body.removeChild(ta);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = value; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch (_) {}
        document.body.removeChild(ta);
      }
    } catch (_) {}
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid="checkout-copy-etransfer-email-btn"
      className={`w-full py-4 rounded font-mono-label text-[12px] inline-flex items-center justify-center gap-2 transition-colors ${copied ? 'bg-emerald-500 text-black' : 'btn-pink'}`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? '✓ COPIED!' : 'COPY E-TRANSFER EMAIL'}
    </button>
  );
};

